import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { Alert, Button, Carousel, Typography } from '@duxton-web/ui';

import '@/features/announcement/index.css';

import { AlertProps } from '@duxton-web/ui/lib';

import { useGetAllActiveBlackdays } from '@/hooks';
import { Blackday } from '@/lib/PlannerDTOs';
import { BROWSER_TARGET } from '@/utils/browser';

export interface Announcement extends AlertProps {
  actionText?: string;
  actionLink?: string;
  actionTarget?: BROWSER_TARGET;
}

interface AnnouncementsProps {
  style?: CSSProperties;
  id?: string;
  items: Announcement[];
}

const enum ANNOUNCEMENTS_STATE {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  OTHERS = 'others',
}

const ANNOUNCEMENTS_STATES = [
  ANNOUNCEMENTS_STATE.ERROR,
  ANNOUNCEMENTS_STATE.WARNING,
  ANNOUNCEMENTS_STATE.INFO,
];

const Description = ({
  desc,
}: {
  desc: ReactNode | string | null;
}): ReactNode => {
  if (!desc) return false;

  if (typeof desc !== 'string') return desc;

  return (
    <Typography.Paragraph ellipsis={{ rows: 2 }}>{desc}</Typography.Paragraph>
  );
};

const Announcements = ({ id, items }: AnnouncementsProps) => {
  const [announcements, setAnnouncements] = useState<ReactNode[]>([]);

  const { data, isSuccess, isError } = useGetAllActiveBlackdays();

  useEffect(() => {
    if (isSuccess) {
      refreshAnnouncements(data);
    } else if (isError) {
      refreshAnnouncements(null);
    }
  }, [isSuccess, isError]);

  function refreshAnnouncements(blackdays?: Blackday[]) {
    const stateMap = new Map<string, ReactNode[]>();

    ANNOUNCEMENTS_STATES.forEach((state: ANNOUNCEMENTS_STATE) => {
      stateMap.set(state, []);
    });

    items.forEach((item: Announcement, index: number) => {
      const {
        showIcon,
        description,
        actionLink,
        actionText,
        actionTarget,
        action,
        ...others
      } = item;
      // Ensure type is one of the valid announcement states
      const type =
        others.type === ANNOUNCEMENTS_STATE.ERROR
          ? ANNOUNCEMENTS_STATE.ERROR
          : others.type === ANNOUNCEMENTS_STATE.WARNING
            ? ANNOUNCEMENTS_STATE.WARNING
            : others.type === ANNOUNCEMENTS_STATE.INFO
              ? ANNOUNCEMENTS_STATE.INFO
              : ANNOUNCEMENTS_STATE.OTHERS;

      let annoucementsList = stateMap.get(type);
      if (!actionLink || !actionText)
        annoucementsList.push(
          <Alert
            key={`annnouncement-${index}`}
            showIcon
            description={<Description desc={description} />}
            action={action}
            {...others}
            className={
              items.length > 1 ? 'announcements-multiple-margin-bottom' : ''
            }
          />,
        );
      else {
        annoucementsList.push(
          <Alert
            key={`annnouncement-${index}`}
            showIcon
            description={<Description desc={description} />}
            {...others}
            action={[
              <Button
                key={`annnouncement-${index}-action`}
                style={{
                  width: 'fit-content',
                }}
                size="small"
                onClick={() => {
                  window.open(actionLink, actionTarget || BROWSER_TARGET.BLANK);
                }}
              >
                {actionText}
              </Button>,
            ]}
            className={
              items.length > 1 ? 'announcements-multiple-margin-bottom' : ''
            }
          />,
        );
      }
      stateMap.set(type, annoucementsList);
    });

    if (blackdays?.length) {
      blackdays.forEach((blackday: Blackday) => {
        const { name, description } = blackday;
        let annoucementsList = stateMap.get(ANNOUNCEMENTS_STATE.WARNING);
        annoucementsList.push(
          <Alert
            key={name}
            showIcon
            type={ANNOUNCEMENTS_STATE.WARNING}
            className="blackday-banner"
            message={`[${name}]`}
            description={<Description desc={description} />}
          />,
        );
        stateMap.set(ANNOUNCEMENTS_STATE.WARNING, annoucementsList);
      });
    }

    // Concatenate arrays in priority order and filter out empty arrays
    const updatedAnnouncements = [
      stateMap.get(ANNOUNCEMENTS_STATE.ERROR) || [],
      stateMap.get(ANNOUNCEMENTS_STATE.WARNING) || [],
      stateMap.get(ANNOUNCEMENTS_STATE.INFO) || [],
      stateMap.get(ANNOUNCEMENTS_STATE.OTHERS) || [],
    ]
      .filter((arr) => arr.length > 0)
      .reduce((acc, curr) => acc.concat(curr), []);

    setAnnouncements(updatedAnnouncements);
  }

  if (announcements?.length == 0) return null;

  return (
    <div
      data-testid="announcements-carousel-wrapper"
      style={{
        flex: '0 1 auto',
      }}
    >
      <Carousel
        className="announcements"
        autoplay
        autoplaySpeed={5000}
        dots={{
          className: 'announcements-carousel-dots',
        }}
        id={id}
        adaptiveHeight
      >
        {announcements}
      </Carousel>
    </div>
  );
};

export default Announcements;
